import { Sparkline } from "./Sparkline";
import { ChevronRight } from "lucide-react"; // Import the icon
import { LongitudinalEntry } from "../../../../types";

// Multi-scale subscale info
interface Subscale {
  name: string;
  score: number;
  displayScore?: string;
  interpretation: string;
  status: "positive" | "negative" | "na";
}

interface DataRowCardProps {
  measureName: string;
  status: "on-track" | "off-track" | "not-applicable";
  lastCompleted: string;
  nextDue: string;
  isOverdue?: boolean;
  onViewDetails?: () => void;
  // Multi-scale support
  subscales?: Subscale[];
  subscaleTrendData?: { [key: string]: number[] }; // Trend data for each subscale
  displaySubscaleNames?: string[]; // New field for filtered subscale names
  completedHistory?: { date: string; responder: string }[];
  longitudinalData?: LongitudinalEntry[];
  statusExplanation?: string;
}

export function DataRowCard({
  measureName,
  status,

  lastCompleted,
  nextDue,
  isOverdue = false,
  onViewDetails,
  subscales = [],
  subscaleTrendData = {},
  displaySubscaleNames,
  completedHistory = [],
  longitudinalData = [],
  statusExplanation,
}: DataRowCardProps) {
  // Helper function to get subscale status color
  const getSubscaleStatusColor = (
    subscaleStatus: "positive" | "negative" | "na",
  ) => {
    return subscaleStatus === "positive" ? "#CA8A04" : "#94A3B8"; // Slate-400 for negative and na
  };

  const testId = `mbc-card-${measureName.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`;

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testId}
      className="relative bg-white rounded-lg shadow-sm border group/card transition-all hover:shadow-md hover:border-slate-400 cursor-pointer hover:z-50 min-w-[1020px]"
      onClick={onViewDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onViewDetails?.();
        }
      }}
      style={{
        borderColor: "#CBD5E1",
      }}
    >
      <div className="grid grid-cols-[200px_minmax(180px,1fr)_140px_140px_200px] gap-6 px-6 py-5 items-center min-w-[1020px]">
        {/* Column 1: Measure Name */}
        <div className="relative flex flex-col gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent double-fire if row is also clickable
              onViewDetails?.();
            }}
            className="flex items-center gap-2 group/btn focus:outline-none"
          >
            <span className="text-lg font-bold text-left text-[#1A202C] group-hover/card:text-[#5A8DAB] group-hover/card:underline decoration-2 underline-offset-4 transition-colors">
              {measureName}
            </span>

            {/* Visual Cue: Sliding Chevron */}
            <div className="bg-blue-50 rounded-full p-1 opacity-0 -translate-x-2 group-hover/card:opacity-100 group-hover/card:translate-x-0 transition-all duration-200">
              <ChevronRight size={16} color="#5A8DAB" strokeWidth={3} />
            </div>
          </button>

          {/* Status Badge */}
          <div className="flex relative group/badge">
            <span
              data-testid={`${testId}-status`}
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider cursor-help ${
                status === "on-track"
                  ? "bg-emerald-100 text-emerald-700"
                  : status === "off-track"
                    ? "bg-red-100 text-red-700"
                    : "bg-amber-100 text-amber-700"
              }`}
            >
              {status.replace("-", " ")}
            </span>
            {/* Status Tooltip */}
            {statusExplanation && (
              <div className="absolute top-full left-0 mt-2 w-max max-w-[250px] bg-slate-800 text-white text-xs rounded py-1 px-2 opacity-0 invisible group-hover/badge:opacity-100 group-hover/badge:visible transition-all duration-200 z-[100] shadow-lg">
                {statusExplanation}
              </div>
            )}
          </div>

          {/* Subtle helper text that appears on hover */}
          <div className="absolute top-full left-0 mt-1 text-xs text-[#5A8DAB] font-medium opacity-0 group-hover/card:opacity-100 transition-opacity pointer-events-none">
            Click to view details
          </div>
        </div>

        {/* Column 2: Latest Result */}
        <div className="flex flex-col gap-1 pointer-events-none">
          {/* Always render subscales now */}
          <div className="flex flex-col gap-3">
            {(displaySubscaleNames
              ? subscales.filter((subscale) =>
                  displaySubscaleNames.includes(subscale.name),
                )
              : subscales
            ).map((subscale, index) => (
              <div key={index} className="flex items-center gap-3">
                {/* Color indicator */}
                <div
                  className="w-1 h-10 rounded-full"
                  style={{
                    backgroundColor: getSubscaleStatusColor(subscale.status),
                  }}
                />

                {/* Score and interpretation */}
                <div className="flex flex-col gap-0.5">
                  <div
                    className="text-xs font-medium"
                    style={{ color: "#475569" }}
                  >
                    {subscale.name === "Average Performance Score"
                      ? "Performance Score"
                      : subscale.name}
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="flex flex-col">
                      <span
                        className="text-[16px] font-bold"
                        style={{ color: "#1A202C" }}
                      >
                        {subscale.interpretation}
                      </span>
                      {/* Historical Indicator: Shows if criteria was ever met at any point in the longitudinal data */}
                      {subscale.interpretation !== "Criteria Met" &&
                        longitudinalData?.some((entry) =>
                          entry.subscales.some(
                            (s) =>
                              s.name === subscale.name &&
                              s.interpretation === "Criteria Met",
                          ),
                        ) && (
                          <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1 rounded uppercase tracking-tighter mt-0.5 w-fit">
                            Met in Past
                          </span>
                        )}
                    </div>
                    <span className="text-[14px] font-semibold text-slate-500">
                      {subscale.displayScore || subscale.score}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Column 3: Schedule (Combined Completed & Due) */}
        <div className="flex flex-col gap-3 pointer-events-none justify-center">
          <div className="flex flex-col gap-0.5">
            <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">
              Last Completed
            </div>
            <div className="text-sm font-bold text-slate-700">
              {lastCompleted || "Pending"}
            </div>
          </div>
          <div className="flex flex-col gap-0.5">
            <div className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">
              Next Due
            </div>
            <div className="text-sm font-bold text-slate-700">{nextDue}</div>
            {isOverdue && (
              <div
                className="text-xs font-bold text-red-600"
                data-testid={`${testId}-overdue`}
              >
                OVERDUE
              </div>
            )}
          </div>
        </div>

        {/* Column 4: Last Completed (History List) */}
        <div className="flex flex-col gap-1 pointer-events-none self-start py-0.5 max-w-fit">
          <div className="flex flex-col gap-1.5">
            {completedHistory.length > 0 ? (
              completedHistory.slice(0, 5).map((entry, idx) => (
                <div
                  key={idx}
                  className="flex justify-start items-center gap-2 border-b border-slate-50 pb-0.5 last:border-0"
                >
                  <span className="text-[11px] font-bold text-slate-700 whitespace-nowrap">
                    {entry.responder}
                  </span>
                  <span className="text-[11px] font-bold text-slate-700 whitespace-nowrap">
                    {entry.date}
                  </span>
                </div>
              ))
            ) : (
              <div className="text-sm font-bold text-slate-700">
                {lastCompleted}
              </div>
            )}
          </div>
        </div>

        {/* Column 5: Trend */}
        <div
          className="h-[80px] flex items-center overflow-hidden"
          style={{ width: "200px", minWidth: 0, maxWidth: "200px" }}
        >
          {Object.keys(subscaleTrendData).length === 0 ? (
            <div className="text-slate-400 font-medium text-[11px] italic w-full text-center bg-slate-50/50 py-4 rounded border border-dashed border-slate-200">
              Trend N/A
            </div>
          ) : (
            /* v8 ignore start - jsdom lacks SVG rendering for Recharts */
            <Sparkline
              width={200}
              height={80}
              data={[...(longitudinalData || [])]
                .sort(
                  (a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime(),
                )
                .map((entry, idx) => {
                  const point: Record<string, unknown> = {
                    id: idx,
                    date: entry.date,
                    responder: entry.responder,
                  };
                  Object.keys(subscaleTrendData).forEach((key) => {
                    point[key] = subscaleTrendData[key][idx];
                  });
                  return point;
                })}
              lines={Object.keys(subscaleTrendData).map((key, i) => ({
                key,
                color: i === 0 ? "#5A8DAB" : i === 1 ? "#10B981" : "#F59E0B",
              }))}
            />
            /* v8 ignore stop */
          )}
        </div>
      </div>
    </div>
  );
}
